import Caret from '@clearkit/icons/glyphs/Caret';
import FacebookIcon from '@clearkit/icons/glyphs/Facebook';
import GoExternal from '@clearkit/icons/glyphs/GoExternal';
import Hubspot from '@clearkit/icons/glyphs/Hubspot';
import LinkedInIcon from '@clearkit/icons/glyphs/Linkedin';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import TwitterIcon from '@clearkit/icons/glyphs/Twitter';
import { CKActionMenu, CKButton, CKPopover } from '@clearkit/react';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import some from 'lodash/some';
import startCase from 'lodash/startCase';
import Flag from 'react-world-flags';

import { dollars, formatNumberWithSuffix } from '~/lib/formatters';

import TwoLineMenuItem from '../Actions/TwoLineMenuItem';
import styles from './styles';

const CompanyDetails = ({
  company,
  salesforceAccountConnector,
  hubspotOwners,
  classes,
  hubspotCompanies,
}) => {
  const {
    city,
    country,
    countryCode,
    description,
    employeesRange,
    estimatedAnnualRevenue,
    facebook,
    subIndustry,
    linkedin,
    location,
    marketCap,
    raised,
    sector,
    tags,
    ticker,
    twitter,
    type,
    salesforceAccounts,
  } = company;

  const opportunities = flatten(
    salesforceAccounts?.map((account) => account.opportunities),
  );

  // Make the company a key on each deal
  const deals = flatten(
    hubspotCompanies?.map((company) =>
      company.deals.map((deal) => ({ ...deal, company })),
    ),
  );

  const visibleDeals = deals.slice(0, 3);
  const moreDeals = deals.slice(3);
  const visibleOpportunities = opportunities.slice(0, 3);
  const moreOpportunities = opportunities.slice(3);

  const [showingMore, setShowingMore] = useState(false);

  return (
    <>
      {some([
        employeesRange,
        type,
        estimatedAnnualRevenue,
        raised,
        ticker,
        marketCap,
        twitter,
        facebook,
        linkedin,
      ]) && (
        <div className="flex flex-shrink-0 px-10 py-5 text-sm border-b border-gray-200 hide-if-empty">
          <div className="flex flex-col flex-shrink-0 pr-8">
            <span className="mb-1 text-xs">Employees</span>
            <span>{employeesRange || <>&mdash;</>}</span>
          </div>
          {type && (
            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs">Type</span>
              <span>{startCase(type)}</span>
            </div>
          )}
          {estimatedAnnualRevenue && (
            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs">Annual Revenue</span>
              <span>{estimatedAnnualRevenue}</span>
            </div>
          )}
          {raised && (
            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs">Raised</span>
              <span>${formatNumberWithSuffix(raised)}</span>
            </div>
          )}

          {ticker && (
            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs">Ticker</span>
              <span>{ticker}</span>
            </div>
          )}

          {marketCap && (
            <div className="flex flex-col flex-shrink-0 pr-8">
              <span className="mb-1 text-xs">Market Cap</span>
              <span>${formatNumberWithSuffix(marketCap)}</span>
            </div>
          )}

          <div className="flex flex-row items-center justify-center ml-auto">
            {twitter && (
              <a
                className="ml-4 text-twitter"
                target="_blank"
                rel="noopener noreferrer"
                href={`https://twitter.com/${twitter}`}>
                <TwitterIcon style={{ width: 20, height: 20 }} />
              </a>
            )}
            {facebook && (
              <a
                className="ml-4 text-facebook"
                target="_blank"
                rel="noopener noreferrer"
                href={`https://facebook.com/${facebook}`}>
                <FacebookIcon style={{ width: 20, height: 20 }} />
              </a>
            )}
            {linkedin && (
              <a
                className="ml-4 text-linkedin"
                target="_blank"
                rel="noopener noreferrer"
                href={`https://linkedin.com/${linkedin}`}>
                <LinkedInIcon style={{ width: 20, height: 20 }} />
              </a>
            )}
          </div>
        </div>
      )}
      {some([country, countryCode, sector, subIndustry, description, tags]) && (
        <div className="flex flex-row justify-between flex-shrink-0 w-full px-10 pt-6 text-sm border-b border-gray-200 pb-7 bg-gray-0">
          <div className="w-full">
            {country && countryCode && (
              <div className="pb-4 border-b border-gray-200">
                <div className="flex flex-row items-center">
                  <div className={classes.flag}>
                    <Flag code={countryCode?.toLowerCase()} />
                  </div>
                  {compact([city, country]).join(', ')}
                </div>
                <p className="pt-1 text-xs text-gray">{location}</p>
              </div>
            )}
            {(sector || subIndustry) && (
              <div
                className={classnames(
                  'border-b border-gray-200 py-4',
                  classes.attributeList,
                )}>
                {sector && (
                  <p>
                    <span className="block mb-2 text-xs text-gray">Sector</span>{' '}
                    {sector}
                  </p>
                )}
                {subIndustry && (
                  <p>
                    <span className="block text-xs text-gray">
                      Sub-Industry
                    </span>{' '}
                    {subIndustry}
                  </p>
                )}
              </div>
            )}
            <div className="mt-4">
              <span className="block mb-2 text-xs text-gray">Description</span>
              <p className="pb-1">
                {showingMore ? description : description?.substr(0, 100)}
              </p>
              {!showingMore && description.length > 100 && (
                <p>
                  <button
                    className="text-xs link-button"
                    onClick={() => setShowingMore(true)}>
                    Show more
                  </button>
                </p>
              )}
            </div>
            {tags && (
              <div className="mt-4">
                <span className="block mb-2 text-xs text-gray">Tags</span>
                <div className="flex flex-wrap">
                  {tags.map((tag) => (
                    <span key={tag} className="my-1 mr-2 ck-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {opportunities?.length > 0 && deals?.length > 0 && (
            <div className="w-8/12 text-gray-500">
              <div className="w-full ck-box">
                <div
                  className={classnames(
                    'px-4 pt-2 border-gray-700 border-ck-box-separator',
                    {
                      'border-b': deals?.length > 0,
                    },
                  )}>
                  {visibleOpportunities.length > 0 && (
                    <>
                      <span className="block py-1 text-xs">
                        {opportunities.length} open Salesforce{' '}
                        {opportunities.length === 1
                          ? 'Opportunity'
                          : 'Opportunities'}
                      </span>
                      {visibleOpportunities.map((opportunity) => (
                        <a
                          key={opportunity.externalId}
                          href={`${salesforceAccountConnector.externalInstanceUrl}/${opportunity.externalId}`}
                          target="blank"
                          className="block mb-2 text-gray-500"
                          rel="noopener noreferrer">
                          <span className="flex flex-row items-center flex-grow">
                            <div
                              className="flex items-center justify-center w-8 h-8 mr-2 rounded-full text-salesforce"
                              style={{
                                background: 'rgb(232, 244, 250)',
                              }}>
                              <Salesforce />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900">
                                {opportunity.name}
                              </span>
                              <span className="text-xs">
                                Owned by{' '}
                                <span className="text-gray-900">
                                  {opportunity.owner?.name || 'Unknown User'}
                                </span>
                              </span>
                            </div>

                            <GoExternal className="ml-auto text-salesforce" />
                          </span>
                        </a>
                      ))}

                      {moreOpportunities.length > 0 && (
                        <CKPopover
                          className="z-10 overflow-y-auto popover-max-height"
                          aria-label="Popover"
                          options={{ placement: 'bottom-start', gutter: 4 }}
                          popover={
                            <CKActionMenu
                              aria-label="Default Menu"
                              hideOnClickOutside={false}
                              visible>
                              {moreOpportunities.map((opportunity) => (
                                <CKActionMenu.Item
                                  key={opportunity.externalId}
                                  as="a"
                                  href={`${salesforceAccountConnector.externalInstanceUrl}/${opportunity.externalId}`}
                                  target="blank"
                                  rel="noopener noreferrer">
                                  <span className="flex flex-row items-center flex-grow">
                                    <TwoLineMenuItem
                                      title={opportunity.name}
                                      subtitle={
                                        <>
                                          Owner &mdash;{' '}
                                          {opportunity.owner?.name ||
                                            'Unknown User'}{' '}
                                          - {dollars(opportunity.amount)}
                                        </>
                                      }
                                    />
                                    <GoExternal className="ml-auto text-salesforce" />
                                  </span>
                                </CKActionMenu.Item>
                              ))}
                            </CKActionMenu>
                          }>
                          <CKButton
                            variant="tertiary"
                            variantColor="blue"
                            rightIcon={<Caret />}
                            className="text-xs">
                            Show {moreOpportunities.length} more opportunit
                            {moreOpportunities.length === 1 ? 'y' : 'ies'}
                          </CKButton>
                        </CKPopover>
                      )}
                    </>
                  )}
                </div>
                <div className="px-4 pt-2">
                  {deals.length > 0 && (
                    <>
                      <span className="block py-1 text-xs">
                        {deals.length} open Hubspot{' '}
                        {deals.length === 1 ? 'Deal' : 'Deals'}
                      </span>
                      {visibleDeals.map((deal) => {
                        const owner = hubspotOwners?.find(
                          (owner) => owner.id === deal.ownerId,
                        );

                        return (
                          <a
                            key={deal.id}
                            href={`https://app.hubspot.com/contacts/${deal.company.externalAccountId}/deal/${deal.id}`}
                            target="blank"
                            className="block mb-2 text-gray-500"
                            rel="noopener noreferrer">
                            <span className="flex flex-row items-center flex-grow">
                              <div
                                className="flex items-center justify-center w-8 h-8 mr-2 rounded-full text-hubspot"
                                style={{ background: 'rgb(253, 243, 240)' }}>
                                <Hubspot />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-900">
                                  {deal?.name}
                                </span>
                                <span className="text-xs">
                                  Owned by{' '}
                                  <span className="text-gray-900">
                                    {owner?.ownerName || 'Unknown Owner'}
                                  </span>
                                </span>
                              </div>

                              <GoExternal className="ml-auto text-hubspot" />
                            </span>
                          </a>
                        );
                      })}

                      {moreDeals.length > 0 && (
                        <CKPopover
                          className="z-10 overflow-y-auto popover-max-height"
                          aria-label="Popover"
                          options={{ placement: 'bottom-start', gutter: 4 }}
                          popover={
                            <CKActionMenu
                              aria-label="Default Menu"
                              hideOnClickOutside={false}
                              visible>
                              {moreDeals?.map((deal) => {
                                const owner = hubspotOwners?.find(
                                  (owner) => owner.id === deal.ownerId,
                                );
                                return (
                                  <CKActionMenu.Item
                                    key={deal.id}
                                    as="a"
                                    href={`https://app.hubspot.com/contacts/${
                                      deal.company.externalAccountId
                                    }/deal/${deal['id']}`}
                                    target="blank"
                                    rel="noopener noreferrer">
                                    <span className="flex flex-row items-center flex-grow">
                                      <TwoLineMenuItem
                                        title={deal.name}
                                        subtitle={
                                          <>
                                            {owner && (
                                              <>
                                                Owner:{' '}
                                                {owner?.ownerName ||
                                                  'Unknown Owner'}
                                              </>
                                            )}
                                            <>
                                              {deal.amount && (
                                                <>
                                                  {' '}
                                                  &mdash; {dollars(deal.amount)}
                                                </>
                                              )}
                                            </>
                                          </>
                                        }
                                      />
                                      <GoExternal className="ml-auto text-hubspot" />
                                    </span>
                                  </CKActionMenu.Item>
                                );
                              })}
                            </CKActionMenu>
                          }>
                          <CKButton
                            variant="tertiary"
                            variantColor="blue"
                            rightIcon={<Caret />}
                            className="text-xs text-hubspot">
                            Show {moreDeals.length} more deal
                            {moreDeals.length === 1 ? '' : 's'}
                          </CKButton>
                        </CKPopover>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

CompanyDetails.propTypes = {
  classes: PropTypes.object,
  salesforceAccountConnector: PropTypes.shape({}),
  hubspotOwners: PropTypes.array,
  hubspotCompanies: PropTypes.array,
  company: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    countryCode: PropTypes.string,
    description: PropTypes.string,
    employeesRange: PropTypes.string,
    estimatedAnnualRevenue: PropTypes.string,
    facebook: PropTypes.string,
    subIndustry: PropTypes.string,
    linkedin: PropTypes.string,
    location: PropTypes.string,
    marketCap: PropTypes.string,
    raised: PropTypes.string,
    sector: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    ticker: PropTypes.string,
    twitter: PropTypes.string,
    type: PropTypes.string,
    salesforceAccounts: PropTypes.array,
  }),
};

export default withStyles(styles)(CompanyDetails);
